import { Injectable, Injector } from '@angular/core';
import { Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { EInvoiceValidationControl } from '@core';
import { ACLService } from '@delon/acl';
import { _HttpClient } from '@delon/theme';
import { Store } from '@ngrx/store';
import { PostcodeLookupService } from '@shared-feature/services/postcode-lookup.service';
import { Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { supplierIsCreatedAction } from 'src/app/actions/supplier';
import { DataBaseService } from 'src/app/shared-feature/services/_base/data-base.service';
import { AutoCodeService } from 'src/app/shared-feature/services/auto-code.service';

@Injectable()
export class SupplierService extends DataBaseService {
  private store: any;
  connecting = false;
  disconnecting = false;
  constructor(injector: Injector) {
    super(injector);
    this.aclKey = 'supplier';
    this.store = this.injector.get(Store);
    this.nextRecordUrl = 'api/suppliers/NextRecordId';
    this.previousRecordUrl = 'api/suppliers/PreviousRecordId';
  }

  get autoCodeSrv() {
    return this.injector.get(AutoCodeService);
  }

  get acl() {
    return this.injector.get(ACLService);
  }

  get router() {
    return this.injector.get(ActivatedRoute);
  }

  get postcodeLookupSrv() {
    return this.injector.get(PostcodeLookupService);
  }

  form = this.createForm();

  get inUse() {
    return <boolean>this.form.controls.inUse.value;
  }

  get deliveryAddresses() {
    return this.form.controls.deliveryAddresses;
  }

  get taxExemptionItems() {
    return this.form.controls.taxExemptionItems;
  }

  get hideZipCodeField() {
    // for MY only
    return this.isEInvRequired && this.form.controls.country.value !== 'MY';
  }

  get isInstantData() {
    const autoPopulate = this.router.snapshot.queryParams['autoPopulate'];
    return autoPopulate ?? false;
  }

  // Requires validation for E-Invoice
  get isEInvRequired(): boolean {
    // If the E-Invoice feature is not enabled, return false
    if (!this.eInvoiceService.isEnabled) return false;

    const reqSupplierValidation =
      this.eInvoiceService.requireSupplierValidation();
    const validationControl = this.form.controls.validationControl.value;
    const isSelfBilled = this.form.controls.isSelfBilled.value;

    if (isSelfBilled) {
      // Refer to EInvoiceValidationControl enum
      // If validation is mandatory, return true
      if (validationControl == EInvoiceValidationControl.FullCompliance)
        return true;
      // If validation is not required, return false
      if (validationControl == EInvoiceValidationControl.DeferredCompliance)
        return false;
      if (validationControl === EInvoiceValidationControl.FollowSettings)
        return reqSupplierValidation;
    }
    // If not self-billed, validation is ignored
    return false;
  }

  get isTINNoRegNoReq() {
    if (this.isEInvRequired && this.form.controls.tinType.value === 1) {
      return true;
    }
    return false;
  }

  get isBillingTabRequired() {
    if (!this.isEInvRequired) {
      return false;
    }

    return (
      // this.form.get('email')?.hasError('required') ||
      this.form.get('phoneNo1')?.hasError('required') ||
      this.form.get('address1')?.hasError('required') ||
      this.form.get('address2')?.hasError('required') ||
      // this.form.get('zipCode')?.hasError('required') ||
      this.form.get('city')?.hasError('required') ||
      this.form.get('state')?.hasError('required') ||
      this.form.get('country')?.hasError('required')
    );
  }

  get isEInvInfoTabRequired() {
    if (!this.isEInvRequired) {
      return false;
    }

    return (
      this.form.get('tinNumber')?.hasError('required') ||
      this.form.get('msicCode')?.hasError('required')
    );
  }

  clone() {
    if (
      this.form.controls.isDeferredTax.value &&
      this.form.controls.enableDeferredVat.value === false
    ) {
      this.msg.error(
        'Cannot clone Deferred VAT transaction while the Deferred VAT option is turned off. Please enable option to continue'
      );
      return;
    }

    let obj = { ...this._data };
    obj.id = 0;
    obj.code = '';
    obj.active = true;
    obj.inUse = false;
    delete obj.legalEntityId;
    return obj;
  }

  new() {
    return this.http.get('api/suppliers/new');
  }

  get(id: string): Observable<any> {
    return this.http.get(`api/suppliers/${id}`);
  }

  create(body: any) {
    return this.http.post('api/suppliers/create/', body).pipe(
      tap(res => {
        if (this.store && this.isInstantData) {
          this.store.dispatch(supplierIsCreatedAction({ newSupplier: res }));
        }
        return res;
      })
    );
  }

  update(body: any) {
    return this.http.post('api/suppliers/update/', body);
  }

  delete(id: string) {
    return new Promise((resolve, reject) => {
      this.http
        .delete(`api/suppliers`, null, { body: { ids: [id] } })
        .subscribe({
          next: res => resolve(res),
          error: () => reject()
        });
    });
  }

  private emailListValidator = (control: any) => {
    const emails = control.value;
    if (!emails || !Array.isArray(emails) || emails.length === 0) {
      return null;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emails.filter(email => !emailRegex.test(email));

    if (invalidEmails.length > 0) {
      return { invalidEmail: { invalidEmails } };
    }

    const totalLength = emails.join(';').length;
    if (totalLength > 500) {
      return { maxLength: { actualLength: totalLength, requiredLength: 500 } };
    }

    return null;
  };

  initForm(data: any) {
    this.buildLines(
      this.form.controls.deliveryAddresses,
      () => this.createAddressForm(),
      data.deliveryAddresses?.length ?? 0
    );

    this.buildLines(
      this.form.controls.taxExemptionItems,
      () => this.createTaxExemptionItemForm(),
      data.taxExemptionItems?.length ?? 0
    );
  }

  override initParam(data: any) {
    // Handle all query parameters for auto-population
    const queryParams = this.router.snapshot.queryParams;

    if (this.id == 0) {
      // Supplier Name
      if (queryParams['SupplierName']) {
        data.name = queryParams['SupplierName'];
      }

      // Supplier Code
      if (queryParams['SupplierCode']) {
        data.code = queryParams['SupplierCode'];
      }

      // Control Account
      if (queryParams['ControlAccountId']) {
        data.controlAccountId = queryParams['ControlAccountId'];
      }

      // Currency
      if (queryParams['CurrencyId']) {
        data.currencyId = queryParams['CurrencyId'];
      }

      // Term
      if (queryParams['TermId']) {
        data.termId = queryParams['TermId'];
      }

      // Purchaser
      if (queryParams['PurchaserId']) {
        data.purchaserId = queryParams['PurchaserId'];
      }

      // Category
      if (queryParams['CategoryId']) {
        data.categoryId = queryParams['CategoryId'];
      }

      // Area
      if (queryParams['AreaId']) {
        data.areaId = queryParams['AreaId'];
      }
    }

    return data;
  }

  override setData(data: any) {
    // Call the parent setData method
    super.setData(data);

    // If this is a new supplier (id == 0) and we have query parameters, load the objects asynchronously
    if (this.id == 0) {
      const queryParams = this.router.snapshot.queryParams;
      this.loadObjectsFromQueryParams(data, queryParams);
    }
  }

  private loadObjectsFromQueryParams(data: any, queryParams: any) {
    const promises: Array<Promise<void>> = [];

    // Control Account
    if (queryParams['ControlAccountId']) {
      promises.push(
        this.loadControlAccountObject(data, queryParams['ControlAccountId'])
      );
    }

    // Currency
    if (queryParams['CurrencyId']) {
      promises.push(this.loadCurrencyObject(data, queryParams['CurrencyId']));
    }

    // Term
    if (queryParams['TermId']) {
      promises.push(this.loadTermObject(data, queryParams['TermId']));
    }

    // Purchaser
    if (queryParams['PurchaserId']) {
      promises.push(this.loadPurchaserObject(data, queryParams['PurchaserId']));
    }

    // Category
    if (queryParams['CategoryId']) {
      promises.push(this.loadCategoryObject(data, queryParams['CategoryId']));
    }

    // Area
    if (queryParams['AreaId']) {
      promises.push(this.loadAreaObject(data, queryParams['AreaId']));
    }

    // Wait for all objects to load, then update the form
    if (promises.length > 0) {
      Promise.all(promises).then(() => {
        // Add a small delay to ensure form controls are ready
        setTimeout(() => {
          // Update the form with the loaded objects
          this.form.patchValue({
            controlAccount: data.controlAccount,
            currency: data.currency,
            term: data.term,
            purchaser: data.purchaser,
            category: data.category,
            area: data.area
          });
          // Force change detection
          this.form.updateValueAndValidity();
        }, 100);
      });
    }
  }

  private loadControlAccountObject(
    data: any,
    controlAccountId: string
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http.get(`api/accountCodes/${controlAccountId}`).subscribe({
        next: (response: any) => {
          data.controlAccount = response;
          resolve();
        },
        error: error => {
          console.error('Error loading control account:', error);
          reject(error);
        }
      });
    });
  }

  private loadCurrencyObject(data: any, currencyId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http.get(`api/currencies/${currencyId}`).subscribe({
        next: (response: any) => {
          data.currency = response;
          resolve();
        },
        error: error => {
          console.error('Error loading currency:', error);
          reject(error);
        }
      });
    });
  }

  private loadTermObject(data: any, termId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http.get(`api/terms/${termId}`).subscribe({
        next: (response: any) => {
          data.term = response;
          resolve();
        },
        error: error => {
          console.error('Error loading term:', error);
          reject(error);
        }
      });
    });
  }

  private loadPurchaserObject(data: any, purchaserId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http.get(`api/purchasers/${purchaserId}`).subscribe({
        next: (response: any) => {
          data.purchaser = response;
          resolve();
        },
        error: error => {
          console.error('Error loading purchaser:', error);
          reject(error);
        }
      });
    });
  }

  private loadCategoryObject(data: any, categoryId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http.get(`api/suppliercategories/${categoryId}`).subscribe({
        next: (response: any) => {
          data.category = response;
          resolve();
        },
        error: error => {
          console.error('Error loading category:', error);
          reject(error);
        }
      });
    });
  }

  private loadAreaObject(data: any, areaId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http.get(`api/areas/${areaId}`).subscribe({
        next: (response: any) => {
          data.area = response;
          resolve();
        },
        error: error => {
          console.error('Error loading area:', error);
          reject(error);
        }
      });
    });
  }

  createForm() {
    const form = this.fb.group({
      id: ['', [Validators.maxLength(20)]],
      code: ['', [Validators.required, Validators.maxLength(20)]],
      name: ['', [Validators.required, Validators.maxLength(250)]],
      name2: ['', [Validators.maxLength(250)]],
      registrationNo: ['', [Validators.maxLength(20)]],
      status: [-1, []],
      country: ['', [Validators.maxLength(100)]],
      state: ['', [Validators.maxLength(100)]],
      city: ['', [Validators.maxLength(50)]],
      address1: ['', [Validators.maxLength(300)]],
      address2: ['', [Validators.maxLength(300)]],
      address3: ['', [Validators.maxLength(100)]],
      address4: ['', [Validators.maxLength(100)]],
      zipCode: ['', [Validators.maxLength(20)]],
      contactPerson: ['', [Validators.maxLength(200)]],
      email: ['', [Validators.maxLength(500)]],
      phoneNo1: ['', [Validators.maxLength(20)]],
      phoneNo2: ['', [Validators.maxLength(20)]],
      faxNo1: ['', [Validators.maxLength(20)]],
      faxNo2: ['', [Validators.maxLength(20)]],
      bizNature: ['', [Validators.maxLength(250)]],
      homePage: ['', [Validators.maxLength(100)]],
      notes: ['', [Validators.maxLength(500)]],
      tinBranchCode: ['', [Validators.maxLength(10)]],
      controlAccountId: ['', []],
      controlAccount: [<any>null, [Validators.required]],
      areaId: [<any>null, []],
      area: [<any>null, []],
      termId: [<any>null, []],
      term: [<any>null, []],
      purchaserId: [<any>null, []],
      purchaser: [<any>null, []],
      currencyId: ['', [Validators.required]],
      currency: [<any>null, [Validators.required]],
      categoryId: [<any>null, []],
      category: [<any>null, []],
      birClassificationId: [<any>null, []],
      birClassification: [<any>null, []],
      isDeferredTax: [false, []],
      startDate: [<any>null, []],
      inUse: [false, []],
      validationControl: [0, []],
      isSelfBilled: [false, []],
      isSelfBilledConsent: [false, []],
      transactionType: [0, []],
      tinType: [1, []],
      tinNumber: ['', [Validators.maxLength(14)]],
      msicCode: ['', [Validators.maxLength(5)]],
      salesTaxRegNo: ['', [Validators.maxLength(35)]],
      serviceTaxRegNo: ['', [Validators.maxLength(35)]],
      tourismTaxRegNo: ['', [Validators.maxLength(17)]],
      frequencyOfBilling: ['', [Validators.maxLength(2)]],
      billingPeriodFrom: [new Date(), []],
      billingPeriodTo: [new Date(), []],
      useBillingAsDOAddress: [false, []],
      signatureUrl: ['', [Validators.maxLength(300)]],
      isPeppolConnect: [false, []],
      peppolId: ['', []],
      taxExemptionItems: this.fb.array([this.createTaxExemptionItemForm()]),
      deliveryAddresses: this.fb.array([this.createAddressForm()]),
      einvoiceEmail: ['', [Validators.maxLength(500)]],
      emailList: [[]],
      einvoiceEmailList: [[]],
      enableDeferredVat: [true, []]
    });

    form.controls.code.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      if (!val) {
        form.controls.code.setValue(`auto-${this.autoCodeSrv.activeFormat.id}`);
      }
    });

    form.controls.controlAccount.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.form.controls.controlAccountId.setValue(val?.id);
    });

    form.controls.purchaser.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.form.controls.purchaserId.setValue(val?.id);
    });

    form.controls.area.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.form.controls.areaId.setValue(val?.id);
    });

    form.controls.term.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.form.controls.termId.setValue(val?.id);
    });

    form.controls.currency.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.form.controls.currencyId.setValue(val?.id);
    });
    form.controls.category.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.form.controls.categoryId.setValue(val?.id);
    });
    form.controls.birClassification.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.form.controls.birClassificationId.setValue(val?.id);
    });

    form.controls.isSelfBilled.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.amendValidators();
    });

    form.controls.country.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;

      if (this.isEInvRequired) {
        if (!this.form.controls.state.value) {
          this.form.controls.state.setValue('Not Applicable');
        }
      }
    });

    form.controls.tinType.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.updateRegNoAndTinValidation();
      this.insertDefaultValue();

      switch (val) {
        case 3:
          form.controls.state.setValue('Not Applicable');
          break;
        case 4:
          form.controls.tinNumber.setValue('EI00000000030');
          form.controls.state.setValue('Not Applicable');
          break;
        default:
          form.controls.tinNumber.setValue('');
          break;
      }
    });

    form.controls.registrationNo.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.updateRegNoAndTinValidation();
    });

    form.controls.tinNumber.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.updateRegNoAndTinValidation();
    });

    form.controls.validationControl.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.updateRegNoAndTinValidation();
      this.amendValidators();
    });

    form.controls.emailList.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      const validEmails = Array.isArray(val)
        ? val.filter((e: any) => e && e.trim())
        : [];
      form.controls.email.setValue(validEmails.join(';'), { emitEvent: false });
    });

    form.controls.einvoiceEmailList.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      const validEmails = Array.isArray(val)
        ? val.filter((e: any) => e && e.trim())
        : [];
      form.controls.einvoiceEmail.setValue(validEmails.join(';'), {
        emitEvent: false
      });
    });

    // Postcode lookup for main form
    form.controls.zipCode.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.lookupAndFillAddress(val, form.controls.city, form.controls.state);
    });

    form.valueChanges.subscribe(() => {
      if (!this.observeChanges) return;
      this.modified = true;
    });

    this.userDefaultService.setControlRequired(
      form.get('purchaser')!,
      'Purchaser'
    );
    this.userDefaultService.setControlRequired(
      form.get('category')!,
      'SupplierCategory'
    );
    this.userDefaultService.setControlRequired(form.get('area')!, 'Area');
    return form;
  }

  updateRegNoAndTinValidation() {
    this.observeChanges = false;

    const registrationNo = this.form.controls.registrationNo;
    const tinNumber = this.form.controls.tinNumber;
    const tinType = this.form.controls.tinType.value ?? -1;

    const processType =
      this.isEInvRequired && tinType >= 1 && tinType <= 6 ? tinType : -1;

    const oneOfTheFieldsPopulated = registrationNo.value || tinNumber.value;

    registrationNo.clearValidators();
    tinNumber.clearValidators();

    var tinValidators = this.isEInvRequired
      ? [Validators.required, Validators.maxLength(14)]
      : [Validators.maxLength(14)];
    const regValidators = (length: number) => {
      return this.isEInvRequired
        ? [Validators.required, Validators.maxLength(length)]
        : [Validators.maxLength(length)];
    };

    switch (processType) {
      case 1:
        registrationNo.setValidators(regValidators(20));
        tinNumber.setValidators(tinValidators);
        break;
      case 2:
        registrationNo.setValidators(regValidators(12));
        tinNumber.setValidators(tinValidators);

        if (oneOfTheFieldsPopulated) {
          registrationNo.removeValidators([Validators.required]);
          tinNumber.removeValidators([Validators.required]);
        }
        break;
      case 3:
        registrationNo.setValidators(regValidators(12));
        tinNumber.setValidators(tinValidators);
        break;
      case 4:
        registrationNo.setValidators(regValidators(12));
        tinNumber.setValidators(tinValidators);

        if (oneOfTheFieldsPopulated) {
          registrationNo.removeValidators([Validators.required]);
          tinNumber.removeValidators([Validators.required]);
        }
        break;
    }
    this.observeChanges = true;
  }

  amendValidators() {
    this.observeChanges = false;
    const tinType = this.form.controls.tinType;

    const msicValidator =
      this.isEInvRequired && tinType.value === 1
        ? [Validators.required, Validators.maxLength(5)]
        : [Validators.maxLength(5)];
    this.form.controls.msicCode.setValidators(msicValidator);
    this.form.controls.msicCode.updateValueAndValidity({ onlySelf: true });

    const addressValidator = this.isEInvRequired
      ? [Validators.required, Validators.maxLength(300)]
      : [Validators.maxLength(300)];
    this.form.controls.address1.setValidators(addressValidator);
    this.form.controls.address1.updateValueAndValidity({ onlySelf: true });

    this.form.controls.address2.setValidators(addressValidator);
    this.form.controls.address2.updateValueAndValidity({ onlySelf: true });

    const stateValidator = this.isEInvRequired
      ? [Validators.required, Validators.maxLength(100)]
      : [Validators.maxLength(100)];
    this.form.controls.state.setValidators(stateValidator);
    this.form.controls.state.updateValueAndValidity({ onlySelf: true });

    const cityValidator = this.isEInvRequired
      ? [Validators.required, Validators.maxLength(50)]
      : [Validators.maxLength(50)];
    this.form.controls.city.setValidators(cityValidator);
    this.form.controls.city.updateValueAndValidity({ onlySelf: true });

    const countryValidator = this.isEInvRequired
      ? [Validators.required, Validators.maxLength(100)]
      : [Validators.maxLength(100)];
    this.form.controls.country.setValidators(countryValidator);
    this.form.controls.country.updateValueAndValidity({ onlySelf: true });

    const phoneValidator = this.isEInvRequired
      ? [Validators.required, Validators.maxLength(20)]
      : [Validators.maxLength(20)];
    this.form.controls.phoneNo1.setValidators(phoneValidator);
    this.form.controls.phoneNo1.updateValueAndValidity({ onlySelf: true });

    // const emailValidator = this.isEInvRequired ? [Validators.required, Validators.email, Validators.maxLength(300)] : [Validators.email, Validators.maxLength(300)];
    // this.form.controls.email.setValidators(emailValidator);
    // this.form.controls.email.updateValueAndValidity({ onlySelf: true });

    this.observeChanges = true;
  }

  insertDefaultValue() {
    const regNo = this.form.controls.registrationNo;
    const tinNo = this.form.controls.tinNumber;
    const tinType = this.form.controls.tinType.value ?? -1;

    const defaultValues: { [key: number]: { tinNo: string; regNo: string } } = {
      2: { tinNo: 'EI00000000010', regNo: '000000000000' },
      3: { tinNo: 'EI00000000030', regNo: 'NA' },
      4: { tinNo: 'EI00000000030', regNo: 'NA' }
    };

    const defaults = defaultValues[tinType] || null;
    if (this.isEInvRequired && defaults) {
      // Assign default values
      if (
        !regNo.value ||
        regNo.value.trim() === '' ||
        regNo.value === 'NA' ||
        regNo.value === '000000000000'
      ) {
        regNo.setValue(defaults.regNo);
      }
      if (
        !tinNo.value ||
        tinNo.value.trim() === '' ||
        ['EI00000000010', 'EI00000000030', 'NA'].includes(tinNo.value)
      ) {
        tinNo.setValue(defaults.tinNo);
      }
    } else {
      // Clear default values
      if (['NA', '000000000000'].includes(regNo.value ?? '')) {
        regNo.setValue(null);
      }
      if (
        ['EI00000000010', 'EI00000000030', 'NA'].includes(tinNo.value ?? '')
      ) {
        tinNo.setValue(null);
      }
    }

    const msicCodeControl = this.form.controls.msicCode;
    if (tinType === 1 && msicCodeControl.value === '00000') {
      // Clear msicCode if tinType is 1 and msicCode is '00000'
      msicCodeControl.patchValue(null);
    }
  }

  createAddressForm() {
    const form = this.fb.group({
      branchCode: ['', [Validators.required, Validators.maxLength(20)]],
      branchName: ['', [Validators.required, Validators.maxLength(250)]],
      contactPerson: ['', [Validators.maxLength(200)]],
      email: ['', [Validators.maxLength(500)]],
      address1: ['', [Validators.required, Validators.maxLength(300)]],
      address2: ['', [Validators.required, Validators.maxLength(300)]],
      address3: ['', [Validators.maxLength(100)]],
      address4: ['', [Validators.maxLength(100)]],
      zipCode: ['', [Validators.maxLength(20)]],
      phoneNo: ['', [Validators.maxLength(100)]],
      faxNo: ['', [Validators.maxLength(100)]],
      registrationNo: ['', [Validators.required, Validators.maxLength(12)]],
      tinNumber: ['', [Validators.required, Validators.maxLength(14)]],
      tinType: [1, []],
      country: ['', [Validators.required, Validators.maxLength(100)]],
      state: ['', [Validators.required, Validators.maxLength(100)]],
      city: ['', [Validators.required, Validators.maxLength(50)]],
      isDefault: [false, []]
    });

    form.controls.country.valueChanges.subscribe(val => {
      //if (!this.observeChanges) return;

      if (this.eInvoiceService.isEnabled) {
        if (val !== 'MY') {
          form.controls.state.setValue('Not Applicable');
        } else {
          if (form.controls.state.value === 'Not Applicable') {
            form.controls.state.setValue(null);
          }
        }
      }
    });

    // Postcode lookup for delivery address form
    form.controls.zipCode.valueChanges.subscribe(val => {
      // Only lookup if country is Malaysia
      const country = form.controls.country?.value;
      if (country === 'MY') {
        this.lookupAndFillAddress(val, form.controls.city, form.controls.state);
      }
    });

    form.controls.tinType.valueChanges.subscribe(val => {
      //if (!this.observeChanges) return;
      switch (val) {
        case 3:
          form.controls.state.setValue('Not Applicable');
          break;
        case 4:
          form.controls.tinNumber.setValue('EI00000000030');
          form.controls.state.setValue('Not Applicable');
          break;
        default:
          break;
      }
    });

    form.valueChanges.subscribe(() => {
      if (!this.observeChanges) return;
      this.modified = true;
    });
    return form;
  }

  createTaxExemptionItemForm() {
    const form = this.fb.group({
      taxExemptionNo: ['', [Validators.required, Validators.maxLength(20)]],
      taxExemptionDetails: ['', [Validators.maxLength(300)]],
      stockId: [<any>null, [Validators.required]],
      stock: [<any>null, [Validators.required]],
      taxCodeId: [<any>null, [Validators.required]],
      taxCode: [<any>null, [Validators.required]]
    });
    form.controls.stock.valueChanges.subscribe(val => {
      // if (!this.observeChanges) return;
      form.controls.stockId.setValue(val?.id);
    });
    form.controls.taxCode.valueChanges.subscribe(val => {
      // if (!this.observeChanges) return;
      form.controls.taxCodeId.setValue(val?.id);
    });

    form.valueChanges.subscribe(() => {
      // if (!this.observeChanges) return;
      this.modified = true;
    });
    return form;
  }

  connectPeppol() {
    this.connecting = true;

    const body: any = {
      type: 'AP',
      id: this.id
    };

    this.http
      .post(`api/StorecovePeppol/ConnectPeppol`, body)
      .pipe(finalize(() => (this.connecting = false)))
      .subscribe(res => {
        this.form.controls.isPeppolConnect.setValue(res);
      });
  }

  disconnectPeppol() {
    this.disconnecting = true;

    const body: any = {
      type: 'AP',
      id: this.id
    };

    this.http
      .post(`api/StorecovePeppol/DisconnectPeppol`, body)
      .pipe(finalize(() => (this.disconnecting = false)))
      .subscribe(res => {
        this.form.controls.isPeppolConnect.setValue(res);
      });
  }

  /**
   * Lookup postcode and auto-fill city and state if found
   * @param zipCode The postal code to lookup
   * @param cityControl The form control for city
   * @param stateControl The form control for state
   */
  private lookupAndFillAddress(
    zipCode: string | null,
    cityControl: any,
    stateControl: any
  ): void {
    // Only proceed if zipCode is 5 digits and country is Malaysia
    if (!zipCode || !/^\d{5}$/.test(zipCode)) {
      return;
    }

    // Only auto-fill for Malaysia
    const country = this.form.controls.country?.value;
    if (country && country !== 'MY') {
      return;
    }

    this.postcodeLookupSrv.lookupPostcode(zipCode).subscribe(result => {
      if (result.found) {
        this.observeChanges = false; // Prevent triggering other valueChanges

        // Only update if current values are empty (don't replace existing values)
        if (!cityControl.value) {
          cityControl.setValue(result.city);
        }

        if (!stateControl.value) {
          stateControl.setValue(result.state);
        }

        this.observeChanges = true;
      } else {
      }
    });
  }
}
